import { useState } from 'react';
import { Link } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { FolderOpenOutlined, FolderOutlined, RightOutlined } from '@ant-design/icons';
import isEmpty from 'lodash/isEmpty';
import sumBy from 'lodash/sumBy';
import compact from 'lodash/compact';
import omit from 'lodash/omit';
import styled from '@emotion/styled';
import type { ProjectTreeNode } from 'types/api.types';
import { useCuratedProjectsTree } from 'stores/rq/projects';
import { useProjectsEnvironmentsStatusesCountsByOrganization } from 'stores/rq/projects-environments-statuses-counts';
import { getAncestorIds, getRedirectUrlForProject, getSubTreeIds } from 'utils/sub-project.utils';
import { Item, onDarkScrollbarCss } from 'components/layout/navbar/navbar-panel.utils';
import { useCloseOnMobile } from 'components/layout/navbar/navbar-panel.hooks';
import { useNavbarCollapse } from 'components/layout/navbar/navbar-collapse-context';
import { typeStyle } from 'constants/typography';

interface NavbarProjectTreeProps {
  projectId: string;
}

const NavbarProjectTree = ({ projectId }: NavbarProjectTreeProps) => {
  const { isCollapsed } = useNavbarCollapse();
  const { activeProjects } = useCuratedProjectsTree();
  const { data: environmentCounts } = useProjectsEnvironmentsStatusesCountsByOrganization(['DRIFTED']);
  const [expandedOverrides, setExpandedOverrides] = useState<Record<string, boolean>>({});
  const [openedPathFor, setOpenedPathFor] = useState(projectId);

  const ancestorIds = getAncestorIds(activeProjects, projectId);
  const root = activeProjects[ancestorIds[0]];

  if (openedPathFor !== projectId) {
    setOpenedPathFor(projectId);
    setExpandedOverrides(overrides => omit(overrides, ancestorIds));
  }

  if (isCollapsed || !root || isEmpty(root.children)) return null;

  const isExpanded = (id: string) => expandedOverrides[id] ?? ancestorIds.includes(id);
  const toggle = (id: string) => setExpandedOverrides(overrides => ({ ...overrides, [id]: !isExpanded(id) }));
  const countActiveEnvironments = (id: string) =>
    sumBy(compact(getSubTreeIds(activeProjects, id).map(subTreeId => environmentCounts?.[subTreeId])), 'total');

  const renderNode = (project: ProjectTreeNode, depth: number) => (
    <li key={project.id}>
      <ProjectTreeRow
        project={project}
        depth={depth}
        isCurrent={project.id === projectId}
        isExpanded={isExpanded(project.id)}
        activeEnvironmentsCount={countActiveEnvironments(project.id)}
        onToggle={() => toggle(project.id)}
      />
      {isExpanded(project.id) && !isEmpty(project.children) && (
        <TreeList>{project.children.map(child => renderNode(child, depth + 1))}</TreeList>
      )}
    </li>
  );

  return (
    <TreeContainer data-e2e="project-tree">
      <TreeTitle>
        <FormattedMessage id="navigation.project.tree.title" />
      </TreeTitle>
      <ScrollableTree data-e2e="project-tree-list">{renderNode(root, 0)}</ScrollableTree>
    </TreeContainer>
  );
};

interface ProjectTreeRowProps {
  project: ProjectTreeNode;
  depth: number;
  isCurrent: boolean;
  isExpanded: boolean;
  activeEnvironmentsCount: number;
  onToggle: () => void;
}

const ProjectTreeRow = ({
  project,
  depth,
  isCurrent,
  isExpanded,
  activeEnvironmentsCount,
  onToggle
}: ProjectTreeRowProps) => {
  const intl = useIntl();
  const closeOnMobile = useCloseOnMobile();
  const hasChildren = !isEmpty(project.children);

  const label = (
    <>
      {isExpanded && hasChildren ? <FolderOpenOutlined /> : <FolderOutlined />}
      <ProjectName>{project.name}</ProjectName>
      {activeEnvironmentsCount > 0 && (
        <EnvironmentsCount data-e2e={`project-tree-count-${project.id}`}>{activeEnvironmentsCount}</EnvironmentsCount>
      )}
    </>
  );

  return (
    <TreeRow active={isCurrent} depth={depth} data-e2e={`project-tree-item-${project.id}`} title={project.name}>
      {project.isReadable ? (
        <RowLink
          to={getRedirectUrlForProject(project)}
          onClick={closeOnMobile}
          aria-current={isCurrent ? 'page' : undefined}>
          {label}
        </RowLink>
      ) : (
        <RowLabel>{label}</RowLabel>
      )}
      {hasChildren ? (
        <ToggleButton
          type="button"
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-label={intl.formatMessage(
            { id: isExpanded ? 'navigation.project.tree.collapse' : 'navigation.project.tree.expand' },
            { name: project.name }
          )}>
          <ToggleIcon isExpanded={isExpanded} />
        </ToggleButton>
      ) : (
        <ToggleSlot data-e2e={`project-tree-toggle-slot-${project.id}`} />
      )}
    </TreeRow>
  );
};

const TreeContainer = styled.nav`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  max-height: 40cqh;
  padding-bottom: var(--spacing-xs);
  margin-bottom: var(--padding-md);
  border-bottom: 1px solid var(--surface-navigation-navigation-item);
`;

const TreeTitle = styled.div`
  flex-shrink: 0;
  padding: var(--padding-xl) var(--padding-md) var(--padding-xs);
  ${typeStyle.labelMd}
  color: var(--text-body-on-dark);
`;

const TreeList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ScrollableTree = styled(TreeList)`
  min-height: 0;
  overflow-y: auto;
  scrollbar-gutter: stable;
  ${onDarkScrollbarCss}
`;

const TreeRow = styled(Item)<{ depth: number }>`
  padding-left: calc(var(--padding-md) + ${({ depth }) => depth * 16}px);
  padding-right: var(--padding-md);
`;

const RowLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  flex: 1;
  min-width: 0;
  color: inherit;

  &:hover {
    color: inherit;
  }
`;

const RowLabel = styled.span`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  flex: 1;
  min-width: 0;
`;

const ProjectName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EnvironmentsCount = styled.span`
  flex-shrink: 0;
  font-size: var(--font-size-labels-label-sm);
  color: var(--text-body-on-dark);
`;

const ToggleSlot = styled.span`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: none;
  cursor: pointer;

  && svg {
    font-size: 10px;
    color: var(--text-body-on-dark);
  }
`;

const ToggleIcon = styled(RightOutlined, {
  shouldForwardProp: propName => propName !== 'isExpanded'
})<{ isExpanded: boolean }>`
  transition: transform 0.15s;
  transform: ${({ isExpanded }) => (isExpanded ? 'rotate(90deg)' : 'none')};
`;

export default NavbarProjectTree;
